import useGetProjectCustomFields from '@/api/queries/project/useGetProjectCustomFields';
import EditCustomFieldsSidebar from '@/ui/components/customFieldGroup/EditCustomFieldsSidebar';
import {
  Accordion,
  AccordionWithChildCount,
  Badge,
  Button,
  Card,
  EmptyState,
  H4,
  Input,
  InputIcon,
  MenuItemCore,
  Text,
  ViewFieldset,
} from '@payaca/ambrosia';

import useGetMyAccountCustomFields from '@/api/queries/me/useGetMyAccountCustomFields';
import useGetMyAccountUsersIncludeDeactivated from '@/api/queries/me/useGetMyAccountUsersIncludeDeactivated';
import {
  AnyCustomFieldDefinition,
  CustomFieldDefinition,
  CustomFieldHelperType,
  CustomFieldType,
  FieldsetCustomFieldDefinition,
} from '@payaca/custom-fields';
import { CustomFieldReadonlyGroup } from '@payaca/custom-fields-react';
import { countPopulatedFields } from './customFieldPopulation';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { GetProjectCustomFieldsQuery } from '@/gql/graphql';
import { CustomFieldGroupRole } from '@payaca/types/custom-fields';
import { Dispatch, FC, useEffect, useReducer, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useGetCustomerCustomFields from '../../../api/queries/customers/useGetCustomerCustomFields';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

/** Entry count from which the collapsed group gets a search box to sift it. */
const SEARCHABLE_FROM = 6;

type StoredCustomField = NonNullable<
  GetProjectCustomFieldsQuery['projectById']['customFields']
>[number];

/**
 * A Fieldset on the Project, Customer or Account, paired with the parsed definition and
 * value its card renders, how many of its fields hold a value, and how to open
 * the edit sidebar.
 */
type CustomFieldEntry = StoredCustomField & {
  definition: FieldsetCustomFieldDefinition;
  parsedValue: CustomFieldHelperType['fieldset']['value'] | null;
  populatedCount: number;
  onEdit: () => void;
};

type SidebarState = {
  activeSidebar: 'editCustomFields' | null;
  fields?: AnyCustomFieldDefinition[];
  value?: Record<string, any>;
  identifier?: CustomFieldDefinition['identifier'];
  label?: CustomFieldDefinition['label'];
};

type SidebarAction =
  | {
      type: 'openEditCustomFields';
      payload: {
        definition: FieldsetCustomFieldDefinition;
        value: CustomFieldHelperType['fieldset']['value'] | null;
      };
    }
  | {
      type: 'closeSidebar';
    };

const sidebarReducer = (
  state: SidebarState,
  action: SidebarAction
): SidebarState => {
  switch (action.type) {
    case 'openEditCustomFields': {
      const { definition, value } = action.payload;

      return {
        activeSidebar: 'editCustomFields',
        fields: [definition],
        value: { [definition.identifier]: value },
        identifier: definition.identifier,
        label: definition.label,
      };
    }
    case 'closeSidebar':
      return { activeSidebar: null };
    default:
      return state;
  }
};

type BaseCustomFieldGroupProps = {
  type: CustomFieldGroupRole;
};
type CustomerCustomFieldGroupProps = {
  type: 'customer';
  customerId: string;
};
type ProjectCustomFieldGroupProps = {
  type: 'project';
  projectId: string;
};
type AccountCustomFieldGroupProps = {
  type: 'account';
};
export type CustomFieldGroupProps = BaseCustomFieldGroupProps &
  (
    | CustomerCustomFieldGroupProps
    | ProjectCustomFieldGroupProps
    | AccountCustomFieldGroupProps
  );
export const CustomFieldGroup: FC<CustomFieldGroupProps> = (props) => {
  const [sidebarState, sidebarDispatch] = useReducer(sidebarReducer, {
    activeSidebar: null,
  });

  const { customFields: projectCustomFields } = useGetProjectCustomFields(
    props.type === 'project' ? props.projectId : undefined
  );
  const { customFields: customerCustomFields } = useGetCustomerCustomFields(
    props.type === 'customer' ? props.customerId : undefined
  );
  const { customFields: accountCustomFields } = useGetMyAccountCustomFields(
    props.type === 'account'
  );
  const customFields = {
    project: projectCustomFields,
    customer: customerCustomFields,
    account: accountCustomFields,
  }[props.type];

  const fieldsets = customFields?.filter(
    (customField) => customField.field.type === 'fieldset'
  );
  // Saving from the finder moves an entry out of the collapsed group, so the
  // control that opened the sidebar unmounts. Filling the last empty entry
  // unmounts the group itself, so focus has to land on this container, which
  // outlives both.
  const containerRef = useRef<HTMLDivElement>(null);

  const closeSidebar = () => {
    sidebarDispatch({ type: 'closeSidebar' });
    containerRef.current?.focus();
  };

  if (!fieldsets?.length) {
    return (
      <Card>
        <Card.Body>
          <AccordionWithChildCount
            title="Custom fields"
            count="0"
            chevronPosition="left"
          >
            <div className="mt-2 flex flex-col gap-4">
              <PermissionGuard
                renderIfHasPermissions={[
                  AccountsPermissions.MANAGE_CUSTOM_FIELD_GROUPS,
                ]}
              >
                <p>
                  <Link
                    to={`/companySettings/custom-data/${props.type}-custom-fields`}
                  >
                    Configure additional fields
                  </Link>
                </p>
              </PermissionGuard>
              <PermissionGuard
                renderIfMissingPermissions={[
                  AccountsPermissions.MANAGE_CUSTOM_FIELD_GROUPS,
                ]}
              >
                <p>Contact admin to configure additional fields</p>
              </PermissionGuard>
            </div>
          </AccordionWithChildCount>
        </Card.Body>
      </Card>
    );
  }

  const entries = fieldsets.map((fieldset) =>
    toEntry(fieldset, sidebarDispatch)
  );
  const populatedEntries = entries.filter((entry) => entry.populatedCount > 0);
  const emptyEntries = entries.filter((entry) => entry.populatedCount === 0);

  return (
    <>
      <div ref={containerRef} tabIndex={-1} className="space-y-4">
        {populatedEntries.map((entry) => (
          <CustomFieldsViewCard
            key={entry.field.identifier}
            label={entry.definition.label}
            fields={entry.definition.children}
            value={entry.parsedValue}
            populatedCount={entry.populatedCount}
            onEdit={entry.onEdit}
          />
        ))}

        {emptyEntries.length > 0 && (
          <UnpopulatedFieldsCard
            entries={emptyEntries}
            hasPopulatedEntries={populatedEntries.length > 0}
          />
        )}
      </div>

      <EditCustomFieldsSidebar
        isOpen={sidebarState.activeSidebar === 'editCustomFields'}
        onClose={closeSidebar}
        fields={sidebarState.fields || []}
        initialValue={sidebarState.value}
        fieldsetIdentifier={sidebarState.identifier}
        fieldsetLabel={sidebarState.label}
        {...props}
      />
    </>
  );
};

const toEntry = (
  customField: StoredCustomField,
  dispatch: Dispatch<SidebarAction>
): CustomFieldEntry => {
  const value = customField.value ? JSON.parse(customField.value) : null;
  const definition = new FieldsetCustomFieldDefinition(
    customField.field.schema
  );

  return {
    ...customField,
    definition,
    parsedValue: value,
    populatedCount: countPopulatedFields(definition.children, value),
    onEdit: () =>
      dispatch({
        type: 'openEditCustomFields',
        payload: { definition, value },
      }),
  };
};

/**
 * The accordion renders its children only while open, so this mounts exactly
 * when the group is opened. Taking focus on mount puts the caret in the box
 * however the group was opened — the accordion's own toggle callback fires on
 * click alone, so it misses a keyboard open.
 */
const FieldsetSearchInput: FC<{
  value: string;
  onChange: (value: string) => void;
}> = (props) => {
  const { value, onChange } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Input
      ref={inputRef}
      type="search"
      aria-label="Search fieldsets"
      leadingElement={<InputIcon name="search-lg.3" />}
      sizeVariant="sm"
      placeholder="Search fieldsets"
      value={value}
      onChange={onChange}
    />
  );
};

const UnpopulatedFieldsCard: FC<{
  entries: CustomFieldEntry[];
  hasPopulatedEntries: boolean;
}> = (props) => {
  const { entries, hasPopulatedEntries } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const query = searchQuery.trim().toLowerCase();

  const noun = entries.length === 1 ? 'fieldset' : 'fieldsets';

  const showSearch = entries.length >= SEARCHABLE_FROM;

  const filteredEntries =
    showSearch && query
      ? entries.filter((entry) =>
          entry.definition.label.toLowerCase().includes(query)
        )
      : entries;

  useEffect(() => {
    if (!showSearch) {
      setSearchQuery('');
    }
  }, [showSearch]);

  return (
    <Card>
      <Card.Body>
        <Accordion
          title={
            <H4>
              {hasPopulatedEntries
                ? `${entries.length} other ${noun}`
                : `${entries.length} ${noun}`}
            </H4>
          }
          chevronPosition="left"
        >
          <div className="mt-2">
            {!hasPopulatedEntries && (
              <Text level="supporting">
                Nothing filled in yet. Choose a fieldset to add data.
              </Text>
            )}

            {showSearch && (
              <div className="my-4">
                <FieldsetSearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
              </div>
            )}

            {/* MenuItemCore rather than CardButton: this already sits inside a
                  Card, and Cards must not nest. */}
            <ul className="list-none p-0 m-0">
              {filteredEntries.map((entry) => (
                <li key={entry.field.identifier}>
                  <MenuItemCore
                    type="button"
                    className="break-words"
                    onClick={entry.onEdit}
                  >
                    {entry.definition.label}
                  </MenuItemCore>
                </li>
              ))}
            </ul>

            {filteredEntries.length === 0 && (
              <EmptyState
                text="No fieldsets match your search"
                className="mt-6"
              />
            )}

            {/* Mounted at all times so the filtered count is announced. */}
            <div className="p-3 pb-1">
              <Text
                as="span"
                level="supporting"
                role="status"
                aria-live="polite"
              >
                {showSearch && query
                  ? `${filteredEntries.length} of ${entries.length} fieldsets shown`
                  : ''}
              </Text>
            </div>
          </div>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

const CustomFieldsViewCard: FC<{
  label: string;
  fields: CustomFieldHelperType[CustomFieldType]['definition'][];
  value: CustomFieldHelperType['fieldset']['value'] | null;
  populatedCount: number;
  onEdit?: () => void;
}> = (props) => {
  const { label, fields, value, populatedCount, onEdit } = props;
  const { data: users } = useGetMyAccountUsersIncludeDeactivated();

  return (
    <Card>
      <Card.Body>
        <Accordion
          title={
            <div className="flex gap-4 items-start grow min-w-0 justify-between">
              <h4>
                {label}
                <Badge className="ml-2" variant="white" size="sm" rounded>
                  {populatedCount}
                </Badge>
              </h4>

              <Button
                className="-m-2"
                variant="secondary"
                size={'sm'}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.();
                }}
              >
                Edit
              </Button>
            </div>
          }
          chevronPosition="left"
        >
          <ViewFieldset className="mt-2">
            <CustomFieldReadonlyGroup
              definitions={fields}
              value={value}
              users={users}
            />
          </ViewFieldset>
        </Accordion>
      </Card.Body>
    </Card>
  );
};
